import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AddressBook, ListChecks } from '@phosphor-icons/react';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="rounded-xl bg-white border border-gray-200 p-4 shadow-sm" data-testid={testId}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tone}`}>
      <Icon size={20} weight="duotone" />
    </div>
    <p className="font-display text-3xl font-bold text-gray-900 leading-none">{value ?? '–'}</p>
    <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mt-2">{label}</p>
  </div>
);

// Desktop Contacts page right side before a contact is picked
export const ContactsGlance = ({ contacts, onSelectContact }) => {
  const { t } = useLanguage();
  const tr = (key, fallback) => (t(key) === key ? fallback : t(key));
  const [openTasks, setOpenTasks] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { all: true, completed: false },
      })
      .then((res) => setOpenTasks((res.data || []).filter((task) => !task.archived)))
      .catch(() => setOpenTasks([]));
  }, []);

  const { withOpen, busiest } = useMemo(() => {
    const counts = {};
    (openTasks || []).forEach((task) => {
      if (task.contact_id) counts[task.contact_id] = (counts[task.contact_id] || 0) + 1;
    });
    const ranked = contacts
      .filter((c) => counts[c.id])
      .map((c) => ({ ...c, openCount: counts[c.id] }))
      .sort((a, b) => b.openCount - a.openCount);
    return { withOpen: openTasks ? ranked.length : null, busiest: ranked.slice(0, 6) };
  }, [contacts, openTasks]);

  return (
    <div className="space-y-5" data-testid="contacts-glance">
      <h2 className="font-display text-2xl font-bold text-gray-900 tracking-tight">{tr('contactsSummary', 'Contacts summary')}</h2>
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={AddressBook} label={tr('totalContacts', 'Total contacts')} value={contacts.length} tone="bg-blue-50 text-blue-600" testId="contacts-glance-total" />
        <Stat icon={ListChecks} label={tr('withOpenTasks', 'With open tasks')} value={withOpen} tone="bg-amber-50 text-amber-600" testId="contacts-glance-with-open" />
      </div>
      <div className="rounded-xl bg-white border border-gray-200 p-3 shadow-sm">
        <p className="px-3 pt-1 pb-2 text-sm font-semibold text-gray-900">{tr('mostOpenTasks', 'Most open tasks')}</p>
        {busiest.length === 0 ? (
          <p className="px-3 pb-2 text-sm text-gray-500">{tr('pickContactHint', 'Select a contact on the left to see their details and tasks.')}</p>
        ) : (
          busiest.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelectContact(c.id)}
              data-testid={`contacts-glance-row-${c.id}`}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-blue-50 text-left transition-colors"
            >
              <span className="truncate text-sm font-medium text-gray-900">{c.name}</span>
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">{c.openCount}</span>
            </button>
          ))
        )}
      </div>
      <p className="text-xs text-gray-400" data-testid="contacts-keyboard-hint">
        {tr('contactsKeyboardHint', 'Tip: use ↑ ↓ to move through your contacts, Enter to open, Esc to close.')}
      </p>
    </div>
  );
};

export default ContactsGlance;
